import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import A_SimpleSelect from "../../../Atoms/Inputs/Selects/A_SimpleSelect";
import A_TextField from "../../../Atoms/Inputs/TextFields/A_TextField";
import A_Button from "../../../Atoms/Inputs/Buttons/A_Button";
import M_ProfessionalPicker from "../../../Molecules/Common/Provision/M_ProfessionalPicker";
import ProfessionalHoverPopover, {
  loadProfessionalDetail,
} from "../../../Molecules/Common/Provision/ProfessionalHoverPopover";
import CircularProgress from "@material-ui/core/CircularProgress";
import {
  isFourthPartyActive,
  defaultFourthPartyContactId,
  fourthPartyContactNumber,
} from "../../../../../domain/provision/provision.rules";
import { FOURTH_PARTY_ROLES, FOURTH_PARTY_ROLE_LABELS } from "../../../../../domain/provision/provision.types";
import { api } from "./Provision.helper";
import useStyles from "./Provision.styles";
import A_SecuredAction from "../../../Atoms/Security/A_SecuredAction";
import { pageSecurityActions } from "../../../../../Utils/Helpers/pageSecurity";

const FOURTH_PARTY_OPTIONS = [
  { value: "", label: "None" },
  ...FOURTH_PARTY_ROLES.map((role) => ({ value: role, label: FOURTH_PARTY_ROLE_LABELS[role] })),
];

/**
 * Sentinel option value for "the contact I want isn't listed" — deliberately not a real id, and
 * never written to the draft: choosing it only reveals the add-a-contact fields.
 */
const ADD_NEW_CONTACT = "__add_new_contact__";

/**
 * Professionals (claude-code-UI-prompt.md §Screen structure 3, DFXX13-19): Lawyer/Valuer/
 * Surveyor type-ahead pickers, plus an optional Fix Charge Receiver/Administrator 4th party
 * whose Company/Contact/Contact Number reveal only once a role is chosen.
 */
const ProfessionalsSection = ({ draft, onChange, editable }) => {
  const classes = useStyles();
  const [namesById, setNamesById] = useState({});
  const [newContactName, setNewContactName] = useState("");
  const [newContactNumber, setNewContactNumber] = useState("");
  const [newCompanyName, setNewCompanyName] = useState("");
  const [addCompanyBusy, setAddCompanyBusy] = useState(false);
  const [addContactBusy, setAddContactBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  // The chosen company's contacts, which back the Contact dropdown (DFXX18) — and which are also
  // what resolves an already-saved contactId back into a name/number/email when the card is
  // re-opened, so the read state shows the saved contact rather than a bare id.
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState(null);
  // True once the user has picked "add a new contact" — the dropdown had no-one suitable.
  const [addingContact, setAddingContact] = useState(false);

  // Resolve a display name for any professional the draft already references by id only (e.g.
  // on first load, before the user has touched a picker) — search results aren't re-fetched for
  // an id we already know, so this only ever runs once per new id. Goes through
  // loadProfessionalDetail (not api.getProfessional) so the hover preview for these same
  // professionals is served from the cache this fills, rather than fetching each one a second
  // time the first time it is hovered.
  useEffect(() => {
    const ids = [draft.lawyerId, draft.valuerId, draft.surveyorId, draft.fourthParty?.companyId].filter(Boolean);
    const missing = ids.filter((id) => !namesById[id]);
    if (missing.length === 0) return;
    Promise.all(missing.map((id) => loadProfessionalDetail(id).catch(() => null))).then((results) => {
      const map = {};
      missing.forEach((id, index) => {
        if (results[index]) map[id] = results[index];
      });
      setNamesById((prev) => ({ ...prev, ...map }));
    });
  }, [draft.lawyerId, draft.valuerId, draft.surveyorId, draft.fourthParty?.companyId]);

  // The three role pickers are React.memo'd, which only pays off if their props are referentially
  // stable — a fresh `setRoleId("lawyerId")` closure on every render would defeat the memo and
  // re-render all three pickers whenever this section re-renders for an unrelated reason (a
  // resolved display name, a 4th-party edit, or a parent-driven render). `draft` is read through a
  // ref so these callbacks never need to be rebuilt when it changes.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const handleLawyerChange = useCallback((option) => {
    onChangeRef.current({ ...draftRef.current, lawyerId: option?.id || null });
    if (option) setNamesById((prev) => ({ ...prev, [option.id]: option }));
  }, []);

  const handleValuerChange = useCallback((option) => {
    onChangeRef.current({ ...draftRef.current, valuerId: option?.id || null });
    if (option) setNamesById((prev) => ({ ...prev, [option.id]: option }));
  }, []);

  const handleSurveyorChange = useCallback((option) => {
    onChangeRef.current({ ...draftRef.current, surveyorId: option?.id || null });
    if (option) setNamesById((prev) => ({ ...prev, [option.id]: option }));
  }, []);

  const fourthParty = draft.fourthParty;
  const active = isFourthPartyActive(fourthParty);

  const handleFourthPartyRoleChange = (role) => {
    if (!role) {
      onChange({ ...draft, fourthParty: null });
      return;
    }
    onChange({
      ...draft,
      fourthParty: { role, companyId: fourthParty?.companyId ?? null, contactId: fourthParty?.contactId ?? null, contactNumber: fourthParty?.contactNumber ?? null },
    });
  };

  const handleCompanyChange = (option) => {
    onChange({
      ...draft,
      // A new company changes the whole 4th party's contact context — reset contact fields too.
      fourthParty: { ...fourthParty, companyId: option?.id || null, contactId: null, contactNumber: null },
    });
    if (option) setNamesById((prev) => ({ ...prev, [option.id]: option }));
    setNewContactName("");
    setNewContactNumber("");
    setContacts([]);
    setAddingContact(false);
    setContactsError(null);
  };

  const handleContactSelect = (value) => {
    if (value === ADD_NEW_CONTACT) {
      // The sentinel is never stored: clear the assignment and open the add-a-contact fields.
      setAddingContact(true);
      onChange({ ...draft, fourthParty: { ...fourthParty, contactId: null, contactNumber: null } });
      return;
    }
    setAddingContact(false);
    const chosen = contacts.find((contact) => contact.id === value);
    onChange({
      ...draft,
      fourthParty: {
        ...fourthParty,
        contactId: value || null,
        contactNumber: value ? fourthPartyContactNumber(chosen, null) : null,
      },
    });
  };

  const handleAddCompany = async () => {
    if (!newCompanyName.trim()) return;
    setAddCompanyBusy(true);
    setActionError(null);
    try {
      const created = await api.createCompany({ name: newCompanyName.trim() });
      handleCompanyChange(created);
      setNewCompanyName("");
    } catch (err) {
      setActionError(err.message || "Couldn't add that company. Please try again.");
    } finally {
      setAddCompanyBusy(false);
    }
  };

  const handleAddContact = async () => {
    if (!fourthParty?.companyId || !newContactName.trim()) return;
    setAddContactBusy(true);
    setActionError(null);
    try {
      const created = await api.createContact(fourthParty.companyId, {
        name: newContactName.trim(),
        contactNumber: newContactNumber.trim() || null,
      });
      // `isMainContact` is the repository's to decide, never this card's: the contract's
      // MAIN-CONTACT RULE has the server return false for anything but a company's first contact,
      // so the created contact is appended AFTER the existing ones and the main contact (if any)
      // keeps its badge and its place at the top of the list.
      setContacts((prev) => (prev.some((contact) => contact.id === created.id) ? prev : [...prev, created]));
      setAddingContact(false);
      onChange({
        ...draft,
        fourthParty: {
          ...fourthParty,
          contactId: created.id,
          contactNumber: fourthPartyContactNumber(created, newContactNumber.trim()),
        },
      });
      setNewContactName("");
      setNewContactNumber("");
    } catch (err) {
      setActionError(err.message || "Couldn't add that contact. Please try again.");
    } finally {
      setAddContactBusy(false);
    }
  };

  const handleCancelAddContact = () => {
    setAddingContact(false);
    setNewContactName("");
    setNewContactNumber("");
    // Fall back to whatever the dropdown would have started on, so the field is never left blank.
    const fallbackId = defaultFourthPartyContactId(contacts);
    if (fallbackId) handleContactSelect(fallbackId);
  };

  const companyId = fourthParty?.companyId || null;

  // The Contact dropdown's options belong to the company, so they are (re)loaded whenever the
  // company changes — including on first open, which is also what turns an already-saved
  // contactId back into a name/number/email to display (DFXX18, DFXX19).
  useEffect(() => {
    if (!companyId) {
      setContacts([]);
      return undefined;
    }
    let cancelled = false;
    setContactsLoading(true);
    setContactsError(null);
    api
      .listCompanyContacts(companyId)
      .then((results) => {
        if (!cancelled) setContacts(Array.isArray(results) ? results : []);
      })
      .catch((err) => {
        if (!cancelled) setContactsError(err);
      })
      .finally(() => {
        if (!cancelled) setContactsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  // Pre-select the company's main (preferred) contact when nothing is assigned yet — "if
  // available, show the main contact by default". Only when the card is editable: a read-only
  // provision must render exactly what was saved, never a default the user never chose. It also
  // stands down while the user is adding a contact, so it can't fight their choice.
  useEffect(() => {
    if (!editable || !companyId || addingContact || fourthParty?.contactId) return;
    const defaultId = defaultFourthPartyContactId(contacts);
    if (defaultId) handleContactSelect(defaultId);
  }, [contacts, companyId, editable, addingContact, fourthParty?.contactId]);

  const companyName = fourthParty?.companyId ? namesById[fourthParty.companyId]?.name : null;
  const selectedContact = fourthParty?.contactId
    ? contacts.find((contact) => contact.id === fourthParty.contactId)
    : null;
  const contactOptions = [
    ...contacts.map((contact) => ({
      value: contact.id,
      label: contact.isMainContact ? `${contact.name} · Main contact` : contact.name,
    })),
    { value: ADD_NEW_CONTACT, label: "+ Add a new contact…" },
  ];

  return (
    <div className={classes.card}>
      <div className={classes.cardHeader}>
        <span className={classes.cardHeaderTitle}>Professionals</span>
      </div>
      <div className={classes.cardBody}>
        <p className={classes.subtle} style={{ marginTop: 0 }}>
          Link the professional parties acting on this loan, drawn from the central repository.
          Hover a selected professional&rsquo;s name for the email address and contact number held
          for them.
        </p>

        {actionError && (
          <div className={classes.validationSummary} style={{ marginBottom: 12 }}>
            {actionError}
          </div>
        )}

        <div className={classes.grid3}>
          <M_ProfessionalPicker
            id="lawyer"
            label="Solicitor / Lawyer"
            type="lawyer"
            value={draft.lawyerId}
            valueLabel={draft.lawyerId ? namesById[draft.lawyerId]?.name : null}
            onChange={handleLawyerChange}
            disabled={!editable}
            showContactPreview
          />
          <M_ProfessionalPicker
            id="valuer"
            label="Valuer"
            type="valuer"
            value={draft.valuerId}
            valueLabel={draft.valuerId ? namesById[draft.valuerId]?.name : null}
            onChange={handleValuerChange}
            disabled={!editable}
            showContactPreview
          />
          <M_ProfessionalPicker
            id="surveyor"
            label="Surveyor"
            type="surveyor"
            value={draft.surveyorId}
            valueLabel={draft.surveyorId ? namesById[draft.surveyorId]?.name : null}
            onChange={handleSurveyorChange}
            disabled={!editable}
            showContactPreview
          />
        </div>

        <div style={{ marginTop: 8, marginBottom: active ? 8 : 0 }}>
          <A_SimpleSelect
            id="fourthPartyRole"
            label="Fix Charge Receiver / Administrator"
            defaultValue={fourthParty?.role || ""}
            options={FOURTH_PARTY_OPTIONS}
            optionLabel="label"
            optionValue="value"
            onChange={handleFourthPartyRoleChange}
            style={{ width: "100%" }}
            disabled={!editable}
          />
        </div>

        {active && (
          <div className={classes.conditional}>
            <div className={classes.sectionHeading}>{FOURTH_PARTY_ROLE_LABELS[fourthParty.role]} details</div>
            <div className={classes.grid3} style={{ marginBottom: 8 }}>
              <div>
                <M_ProfessionalPicker
                  id="fourthPartyCompany"
                  label="Company"
                  type="company"
                  value={fourthParty.companyId}
                  valueLabel={companyName}
                  onChange={handleCompanyChange}
                  disabled={!editable}
                  placeholder="Search repository…"
                  showContactPreview
                />
                {editable && !fourthParty.companyId && (
                  <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                    <A_TextField
                      id="newCompanyName"
                      inputProps={{ placeholder: "…or add a new company" }}
                      defaultValue={newCompanyName}
                      onChange={(e) => setNewCompanyName(e.target.value)}
                      variant="outlined"
                      size="small"
                      fullWidth
                    />
                    <A_SecuredAction permission={pageSecurityActions.CREATE}>
                      <A_Button label="Add" size="small" onClick={handleAddCompany} disabled={addCompanyBusy || !newCompanyName.trim()} />
                    </A_SecuredAction>
                  </div>
                )}
              </div>
              <div>
                {/* GET /companies/{companyId}/contacts (contract v0.2.0) backs this: the company's
                    contacts are the selectable options, main contact first and pre-selected, with
                    "+ Add a new contact…" as the escape hatch when none of them is the right
                    person (DFXX18). */}
                {/* Same hover preview as the pickers, over the field that displays the chosen
                    contact's name (OQ2). listCompanyContacts already returned this contact's
                    email and number, so they are handed straight to the preview and hovering
                    costs no request — which is why neither is printed inline any more. */}
                <ProfessionalHoverPopover
                  name={selectedContact?.name}
                  detail={selectedContact}
                  disabled={!selectedContact || addingContact}
                >
                  <A_SimpleSelect
                    id="fourthPartyContact"
                    label="Contact"
                    defaultValue={addingContact ? ADD_NEW_CONTACT : fourthParty.contactId || ""}
                    options={contactOptions}
                    optionLabel="label"
                    optionValue="value"
                    onChange={handleContactSelect}
                    style={{ width: "100%" }}
                    disabled={!editable || !fourthParty.companyId || contactsLoading}
                  />
                </ProfessionalHoverPopover>
                {!fourthParty.companyId && <div className={classes.hint}>Choose a company first.</div>}
                {contactsLoading && (
                  <div className={classes.hint} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <CircularProgress size={12} /> Loading contacts…
                  </div>
                )}
                {contactsError && (
                  <div className={classes.hint}>Couldn&rsquo;t load this company&rsquo;s contacts. You can still add one.</div>
                )}
                {fourthParty.companyId && !contactsLoading && !contactsError && contacts.length === 0 && !addingContact && (
                  <div className={classes.hint}>No contacts held for this company yet. Add one.</div>
                )}
                {selectedContact && !addingContact && (
                  <div className={classes.hint}>{selectedContact.isMainContact ? "Main contact" : "Contact"}</div>
                )}
                {addingContact && (
                  <>
                    <A_TextField
                      id="newContactName"
                      label="New contact name"
                      inputType="value"
                      defaultValue={newContactName}
                      onChange={(e) => setNewContactName(e.target.value)}
                      variant="outlined"
                      size="small"
                      margin="dense"
                      fullWidth
                      inputProps={{ placeholder: "Full name" }}
                    />
                    <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                      <A_SecuredAction permission={pageSecurityActions.UPDATE}>
                        <A_Button
                          label="Save contact"
                          size="small"
                          onClick={handleAddContact}
                          disabled={addContactBusy || !newContactName.trim()}
                        />
                      </A_SecuredAction>
                      <A_Button label="Cancel" size="small" onClick={handleCancelAddContact} disabled={addContactBusy} />
                    </div>
                    {/* Per the contract's MAIN-CONTACT RULE the repository saves this contact
                        without making it the company's main one. */}
                    <div className={classes.hint} style={{ marginTop: 4 }}>
                      Saved to the repository, but not as the company&rsquo;s main contact.
                    </div>
                  </>
                )}
              </div>
              {/* Controlled (inputType="value") so it re-renders with the repository's number as
                  soon as a different contact is picked — an uncontrolled defaultValue would keep
                  showing the previous contact's number. It stays editable: the number lives on the
                  loan's fourthParty, so a per-loan correction doesn't touch the repository. */}
              <A_TextField
                id="fourthPartyContactNumber"
                label="Contact Number"
                inputType="value"
                inputProps={{ placeholder: "e.g. +44 20 7946 0000" }}
                defaultValue={(addingContact ? newContactNumber : fourthParty.contactNumber) || ""}
                onChange={(e) => {
                  if (addingContact) setNewContactNumber(e.target.value);
                  else onChange({ ...draft, fourthParty: { ...fourthParty, contactNumber: e.target.value } });
                }}
                disabled={!editable || !fourthParty.companyId}
                variant="outlined"
                size="small"
                margin="dense"
                fullWidth
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

ProfessionalsSection.propTypes = {
  draft: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  editable: PropTypes.bool,
};

// Memoized: Provision.jsx holds the Provision Details draft, the Professionals draft and the notes
// draft as three sibling useState values in ONE component, so every keystroke in Provision Amount /
// Approval Date / GCE Net of Provision re-rendered this entire section and all three of its
// pickers. Nothing here depends on those fields, so the render was pure waste — and it is what made
// typing feel like it froze the card. `draft` only changes identity when a professional actually
// changes, and `onChange` is Provision's own setProfessionalsDraft (stable), so this holds.
export default React.memo(ProfessionalsSection);
